import { postTrackingData } from "@/api/endpoints/track";
import clusterTimes from "@/containers/Episode/utils/clusterTimes";
import { EVENT } from "@/tracking_events";
import { Episode } from "@/types/Episode";
import { AudioMention } from "@/types/Mention";

import SimpleAudio, { Fragment } from "../../../Audio/SimpleAudio";

/**
 * EpisodeAudio
 * This is a thin wrapper for the audio player adding support for our episodes,
 * leaving the audio player itself not knowing about our episodes.
 */

class EpisodeAudio extends SimpleAudio {
  id: string;

  private setupTracking = () => {
    this.listen("play", () => this.track());
  };

  constructor(public episode: Episode & { audioMentions: AudioMention[] }) {
    const clusters = episode.grouped_times_and_words
      ? (clusterTimes(episode.grouped_times_and_words.times).filter(
          (cluster) => cluster.length === 2
        ) as Fragment[])
      : [];

    const fragments: Fragment[] = clusters;
    super({ src: episode.audio_url || "", fragments });

    /** Setup tracking */
    this.setupTracking();

    /** Set some useful constants */
    this.id = episode.episode_uuid;
  }

  // Bind the original skipToPrevious/skipToNextFragment
  // TODO: Fix these expects
  //@ts-expect-error - We're overriding the original function, so we need to bind it.
  _original_skipToNextFragment = this.skipToNextFragment.bind(this);
  //@ts-expect-error - We're overriding the original function, so we need to bind it.
  _original_skipToPreviousFragment = this.skipToPreviousFragment.bind(this);

  // We're modifying the default skipToPreviousFragment/skipToNextFragment function to
  // instead skip to the start of the quotes in th the trimmed content.
  skipToPreviousFragment = () => {
    const previous = this.getPreviousFragment();

    // If there's only one mention jump to the previous fragment not caring about the mention timings.
    if (this.episode.audioMentions.length === 1) {
      this._original_skipToPreviousFragment();
      return;
    }

    if (previous && this.state.fragments.length > 0) {
      let closest: number | null = null;
      let closestMention: AudioMention | null = null;
      const [previousStart] = previous;

      // Find the currently playing mention
      const currentMention = this.episode.audioMentions.find((a) => {
        return (
          a.context_start_timing <= this.state.currentTime &&
          a.context_end_timing >= this.state.currentTime
        );
      });

      // If we've found the current mention and not surpassed it's end time, skip to the previous fragment
      // the previous fragment in this case is _within_ the current mention. For example, music that has been cut away.
      if (
        !currentMention || // we're proboably (timing-wise) in a mention context, but after music.
        (currentMention && currentMention.context_end_timing > previousStart)
      ) {
        this._original_skipToPreviousFragment();
        return;
      }

      // Find the closest mention to the previous fragment
      this.episode.audioMentions.forEach((m: AudioMention) => {
        const distance = Math.abs(previousStart - m.context_start_timing);

        if (
          closest === null ||
          (m.context_start_timing >= previousStart && distance < closest)
        ) {
          closestMention = m;
          closest = distance;
        }

        // Skip to the context start of that fragment

        closestMention?.context_start_timing &&
          this.play(closestMention.context_start_timing);
      });
    }
  };

  skipToNextFragment = () => {
    const next = this.getNextFragment();

    // If there's only one mention jump to the next fragment not caring about the mention timings.
    if (this.episode.audioMentions.length === 1) {
      this._original_skipToNextFragment();
      return;
    }

    if (next && this.state.fragments.length > 0) {
      let closest: number | null = null;
      let closestMention: AudioMention | null = null;
      const [nextStart] = next;

      // Find the currently playing mention
      const currentMention = this.episode.audioMentions.find((a) => {
        return (
          a.context_start_timing <= this.state.currentTime &&
          a.context_end_timing >= this.state.currentTime
        );
      });

      // If we've found the current mention and not surpassed it's end time, skip to the next fragment
      // the next fragment in this case is _within_ the current mention. For example, music that has been cut away.
      if (
        !currentMention || // we're proboably (timing-wise) in a mention context, but after music.
        (currentMention && currentMention.context_end_timing > nextStart)
      ) {
        this._original_skipToNextFragment();
        return;
      }

      // Find the closest mention to the next fragment
      this.episode.audioMentions.forEach((m: AudioMention) => {
        const distance = Math.abs(nextStart - m.context_start_timing);

        if (
          closest === null ||
          (m.context_start_timing >= nextStart && distance < closest)
        ) {
          closestMention = m;
          closest = distance;
        }

        // Skip to the context start of that fragment

        closestMention?.context_start_timing &&
          this.play(closestMention.context_start_timing);
      });
    }
  };

  track() {
    const episode = this.episode;

    postTrackingData(EVENT.PLAYED_MENTION, {
      "Channel Name": episode.show,
      "Channel Type": episode.source?.toLowerCase(),
      "Episode Title": episode.title,
      "Mention URL": episode.share_url,
      Source: episode.website_url,
    });
  }
}
export default EpisodeAudio;
